/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: LogosTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the branding module.
----------------------------------------------------------------*/


import React from 'react';
import { Image, Sun, Moon, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SimpleTooltip from '@/components/ui/SimpleTooltip';
import { useI18n } from '@/hooks/useI18n';
import type { BrandingImageField, TabProps } from './types';

// ---------------------------------------------------------------------------
// ImageUploadCard sub-component
// ---------------------------------------------------------------------------

interface ImageUploadCardProps {
  /** Image field key in the branding data model */
  field: BrandingImageField;
  /** Unique HTML id for the hidden file input (must be unique per page) */
  inputId: string;
  /** Optional tooltip description for the label */
  tooltip?: string;
  /** Label displayed above the image slot */
  label: React.ReactNode;
  /** Data URL preview (from FileReader) or null before any upload */
  preview: string | null | undefined;
  /** Persisted URL from the database, or null when not set */
  savedValue: string | null | undefined;
  /** Whether *this* field is currently being uploaded */
  isUploading: boolean;
  /** Text label for the upload/change button */
  uploadLabel: string;
  /** Text label for the change button when an image exists */
  changeLabel: string;
  /** Text label for the remove button */
  removeLabel: string;
  /** Empty-state message shown in the placeholder area */
  emptyMessage: string;
  /** Called when the user selects a file */
  onUpload: (field: BrandingImageField, file: File) => void;
  /** Called when the user clicks "Remove" */
  onRemove: (field: BrandingImageField) => void;
  /** CSS class for the preview image (controls display size) */
  imageClassName?: string;
}

/**
 * ImageUploadCard
 *
 * Reusable card for a single branding image slot. Renders either a preview or
 * an empty-state placeholder, along with upload/change/remove action buttons.
 *
 * The hidden `<input type="file">` is triggered programmatically via the
 * visible button to keep the native file-picker UI accessible.
 */
function ImageUploadCard({
  field,
  inputId,
  tooltip,
  label,
  preview,
  savedValue,
  isUploading,
  uploadLabel,
  changeLabel,
  removeLabel,
  emptyMessage,
  onUpload,
  onRemove,
  imageClassName = 'h-16 w-auto mx-auto object-contain',
}: ImageUploadCardProps) {
  const hasImage = !!(preview || savedValue);
  const imageSrc = preview || savedValue || '';

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(field, file);
      // Reset the input so re-selecting the same file triggers onChange again
      e.target.value = '';
    }
  };

  const triggerPicker = () => {
    (document.getElementById(inputId) as HTMLInputElement | null)?.click();
  };

  return (
    <div className="space-y-2">
      {/* Label � wrapped in tooltip when description provided */}
      {tooltip ? (
        <SimpleTooltip content={tooltip} position="top" maxWidth="300px">
          <label className="text-sm font-medium text-foreground flex items-center gap-2">
            {label}
          </label>
        </SimpleTooltip>
      ) : (
        <label className="text-sm font-medium text-foreground flex items-center gap-2">
          {label}
        </label>
      )}

      {/* Image slot */}
      <div className="border border-border rounded-lg p-3 bg-muted/20">
        {hasImage ? (
          <div className="space-y-2">
            <img src={imageSrc} alt="" className={imageClassName} />
            {/* Action buttons when image exists */}
            <div className="flex justify-center gap-1">
              <Button
                variant="outline" size="sm"
                onClick={triggerPicker}
                className="text-xs px-2 py-1 h-7"
                disabled={isUploading}
              >
                <Upload className="mr-1 h-3 w-3" />
                {changeLabel}
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => onRemove(field)}
                className="text-xs px-2 py-1 h-7"
              >
                <X className="mr-1 h-3 w-3" />
                {removeLabel}
              </Button>
            </div>
          </div>
        ) : (
          /* Empty state placeholder */
          <div className="text-center py-4">
            <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
            <p className="text-xs text-muted-foreground mb-2">{emptyMessage}</p>
            <div className="flex justify-center gap-1">
              <Button
                variant="outline" size="sm"
                onClick={triggerPicker}
                className="text-xs px-2 py-1 h-7"
                disabled={isUploading}
              >
                <Upload className="mr-1 h-3 w-3" />
                {uploadLabel}
              </Button>
            </div>
          </div>
        )}

        {/* Hidden native file input */}
        <input
          id={inputId}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LogosTab
// ---------------------------------------------------------------------------

/**
 * LogosTab
 *
 * Renders the "Logos & Icons" tab inside the Branding form view.
 * Exposes upload/remove controls for all 6 image fields via `ImageUploadCard`.
 */
export const LogosTab: React.FC<TabProps> = ({
  formData,
  onFileUpload,
  onRemoveFile,
  previews,
  uploadingField,
}) => {
  const { t } = useI18n();

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
        <Image className="h-5 w-5" />
        {t('branding.logosAndIcons', 'Logos & Icons')}
      </h3>

      <div className="space-y-8">
        {/* -- Desktop Logos ----------------------------------------------- */}
        <div>
          <h4 className="text-md font-medium text-foreground mb-4">
            {t('branding.sections.desktopLogos', 'Desktop Logos')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ImageUploadCard
              field="logo"
              inputId="branding-logo-upload"
              tooltip={t('branding.tooltips.logo', 'Upload your tenant logo for light mode. Recommended size: 200�200px or larger.')}
              label={<><Sun className="h-4 w-4" />{t('branding.logoLight', 'Logo (Light Mode)')}</>}
              preview={previews.logo}
              savedValue={formData.logo}
              isUploading={uploadingField === 'logo'}
              uploadLabel={t('branding.actions.uploadLogo', 'Upload logo')}
              changeLabel={t('branding.actions.change', 'Change')}
              removeLabel={t('branding.actions.remove', 'Remove')}
              emptyMessage={t('branding.emptyStates.noLogo', 'No logo uploaded')}
              onUpload={onFileUpload}
              onRemove={onRemoveFile}
            />
            <ImageUploadCard
              field="logoDark"
              inputId="branding-logo-dark-upload"
              tooltip={t('branding.tooltips.logoDark', 'Upload your tenant logo for dark mode. Recommended size: 200�200px or larger.')}
              label={<><Moon className="h-4 w-4" />{t('branding.logoDark', 'Logo (Dark Mode)')}</>}
              preview={previews.logoDark}
              savedValue={formData.logoDark}
              isUploading={uploadingField === 'logoDark'}
              uploadLabel={t('branding.actions.uploadLogoDark', 'Upload dark logo')}
              changeLabel={t('branding.actions.change', 'Change')}
              removeLabel={t('branding.actions.remove', 'Remove')}
              emptyMessage={t('branding.emptyStates.noDarkLogo', 'No dark logo uploaded')}
              onUpload={onFileUpload}
              onRemove={onRemoveFile}
            />
          </div>
        </div>

        {/* -- Mobile Logos & Favicons ------------------------------------- */}
        <div>
          <h4 className="text-md font-medium text-foreground mb-4">
            {t('branding.sections.mobileLogosAndFavicons', 'Mobile Logos & Favicons')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <ImageUploadCard
              field="mobileLogo"
              inputId="branding-mobile-logo-upload"
              tooltip={t('branding.tooltips.mobileLogo', 'Upload your mobile logo for light mode. Recommended size: 512�512px square.')}
              label={<><Sun className="h-3 w-3" />{t('branding.labels.mobileLight', 'Mobile (Light)')}</>}
              preview={previews.mobileLogo}
              savedValue={formData.mobileLogo}
              isUploading={uploadingField === 'mobileLogo'}
              uploadLabel={t('branding.actions.upload', 'Upload')}
              changeLabel={t('branding.actions.change', 'Change')}
              removeLabel={t('branding.actions.remove', 'Remove')}
              emptyMessage={t('branding.emptyStates.noMobileLogo', 'No mobile logo')}
              onUpload={onFileUpload}
              onRemove={onRemoveFile}
              imageClassName="h-10 w-10 mx-auto object-contain rounded"
            />
            <ImageUploadCard
              field="mobileLogoDark"
              inputId="branding-mobile-logo-dark-upload"
              tooltip={t('branding.tooltips.mobileLogoDark', 'Upload your mobile logo for dark mode. Recommended size: 512�512px square.')}
              label={<><Moon className="h-3 w-3" />{t('branding.labels.mobileDark', 'Mobile (Dark)')}</>}
              preview={previews.mobileLogoDark}
              savedValue={formData.mobileLogoDark}
              isUploading={uploadingField === 'mobileLogoDark'}
              uploadLabel={t('branding.actions.upload', 'Upload')}
              changeLabel={t('branding.actions.change', 'Change')}
              removeLabel={t('branding.actions.remove', 'Remove')}
              emptyMessage={t('branding.emptyStates.noMobileLogoDark', 'No dark mobile logo')}
              onUpload={onFileUpload}
              onRemove={onRemoveFile}
              imageClassName="h-10 w-10 mx-auto object-contain rounded"
            />
            <ImageUploadCard
              field="favicon"
              inputId="branding-favicon-upload"
              tooltip={t('branding.tooltips.favicon', 'Upload your favicon for light mode. Recommended size: 32�32px or 16�16px.')}
              label={<><Sun className="h-3 w-3" />{t('branding.labels.faviconLight', 'Favicon (Light)')}</>}
              preview={previews.favicon}
              savedValue={formData.favicon}
              isUploading={uploadingField === 'favicon'}
              uploadLabel={t('branding.actions.upload', 'Upload')}
              changeLabel={t('branding.actions.change', 'Change')}
              removeLabel={t('branding.actions.remove', 'Remove')}
              emptyMessage={t('branding.emptyStates.noFavicon', 'No favicon')}
              onUpload={onFileUpload}
              onRemove={onRemoveFile}
              imageClassName="h-6 w-6 mx-auto object-contain"
            />
            <ImageUploadCard
              field="faviconDark"
              inputId="branding-favicon-dark-upload"
              tooltip={t('branding.tooltips.faviconDark', 'Upload your favicon for dark mode. Recommended size: 32�32px or 16�16px.')}
              label={<><Moon className="h-3 w-3" />{t('branding.labels.faviconDark', 'Favicon (Dark)')}</>}
              preview={previews.faviconDark}
              savedValue={formData.faviconDark}
              isUploading={uploadingField === 'faviconDark'}
              uploadLabel={t('branding.actions.upload', 'Upload')}
              changeLabel={t('branding.actions.change', 'Change')}
              removeLabel={t('branding.actions.remove', 'Remove')}
              emptyMessage={t('branding.emptyStates.noFaviconDark', 'No dark favicon')}
              onUpload={onFileUpload}
              onRemove={onRemoveFile}
              imageClassName="h-6 w-6 mx-auto object-contain"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
